import { IGetUserResponse, IResetUserPasswordRequest, ISetUserRoleRequest, IUserSettingRequest, UserSettingEntity } from './types';
import { baseSettingsApi, SettingItemEntity, SettingUnitType } from '@src/core/features';
import { isUnitType, setUnitType } from '@src/core/features/settings/units';
import { MeasurementType, SETTING_MEASUREMENT_MAP } from '@src/core/features/settings/entities';
import { toSi } from '@src/core/extensions';
import { UnitSystemType } from '@src/core/enums';

// Settings inputs are edited in the user's display unit, but the wire is always SI.
// Convert each dimensional value display -> SI and tag every item with
// unitSystem=METRIC so the backend treats the value as SI (no org-unit guesswork).
// Non-dimensional settings (units, failsafe, modes) pass through untouched.
// UNITS Standard R3/R4.
const toWireSetting = (item: SettingItemEntity) => {
  const measurement = SETTING_MEASUREMENT_MAP[item.key] ?? MeasurementType.NONE;
  if (measurement === MeasurementType.NONE) {
    return { ...item, unitSystem: UnitSystemType.METRIC };
  }
  const si = toSi(item.value, measurement);
  return {
    ...item,
    value: Number.isFinite(si) ? String(si) : item.value,
    unitSystem: UnitSystemType.METRIC,
  };
};


export const settingApi = baseSettingsApi.injectEndpoints({
  endpoints: (builder) => ({
    setSettings: builder.mutation<void, SettingItemEntity[]>({
      query: (data) => ({
        url: '/api/v1/orders/settings',
        method: 'POST',
        data: data.map(toWireSetting),
      }),
      invalidatesTags: ['Settings'],
      async onQueryStarted(payload, { queryFulfilled }) {
        // Optimistically apply the new units to the local store so the weather
        // widget (and any other consumer of useUnitType) re-renders without
        // waiting for the Settings refetch. The backend rebroadcasts cached
        // weather right after this POST returns; without the optimistic update
        // that WS push would race the refetch and the widget could redraw with
        // the stale unit.
        const unitsItem = payload.find((item) => item.key === SettingUnitType.UNITS);
        if (unitsItem && isUnitType(unitsItem.value)) {
          setUnitType(unitsItem.value);
        }
        try {
          await queryFulfilled;
        } catch {
          // UnitsProvider realigns from getSettings after successful refetch.
        }
      },
    }),
    getUsers: builder.query<IGetUserResponse, void>({
      query: () => ({
        url: '/api/v1/user',
        method: 'GET',
      }),
      providesTags: ['Users'],
    }),
    createUser: builder.mutation({
      query: (data) => ({
        url: '/api/v1/user',
        method: 'POST',
        data,
      })
    }),
    getUserRoles: builder.query<string[], void>({
      query: () => ({
        url: '/api/v1/user/role',
        method: 'GET',
      })
    }),
    getAllRoles: builder.query<string[], void>({
      query: () => ({
        url: '/api/v1/user/roles',
        method: 'GET',
      })
    }),
    setUserRole: builder.mutation<void, ISetUserRoleRequest>({
      query: (data) => ({
        url: '/api/v1/user/role',
        method: 'PUT',
        data
      }),
      invalidatesTags: ['Users'],
    }),
    resetUserPassword: builder.mutation<void, IResetUserPasswordRequest>({
      query: ({ id, data }) => ({
        url: `/api/v1/user/password/${id}`,
        method: 'POST',
        data,
      }),
      invalidatesTags: ['Users'],
    }),
    deleteUser: builder.mutation<void, number>({
      query: (userId) => ({
        url: `/api/v1/user/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Users'],
    }),

    // user-settings (key-value storage)
    getUserSetting: builder.query<UserSettingEntity, string>({
      query: (key) => ({
        url: `/api/v1/user/settings/${key}`,
        method: 'GET',
      }),
      transformResponse: (response: UserSettingEntity): UserSettingEntity => {
        try {
          const parsed = JSON.parse(response.value);
          if (typeof parsed === 'string') {
            return { ...response, value: parsed };
          }
        } catch { /* value is not JSON-encoded, use as-is */ }
        return response;
      },
      providesTags: (_result, _error, key) => [{ type: 'UserSettings' as const, id: key }],
    }),
    createUserSetting: builder.mutation<void, IUserSettingRequest>({
      query: ({ key, value }) => ({
        url: `/api/v1/user/settings/${key}`,
        method: 'POST',
        data: value,
      }),
      invalidatesTags: (_result, _error, { key }) => [{ type: 'UserSettings' as const, id: key }],
    }),
    updateUserSetting: builder.mutation<void, IUserSettingRequest>({
      query: ({ key, value }) => ({
        url: `/api/v1/user/settings/${key}`,
        method: 'PUT',
        data: value,
      }),
      invalidatesTags: (_result, _error, { key }) => [{ type: 'UserSettings' as const, id: key }],
    }),
    deleteUserSetting: builder.mutation<void, string>({
      query: (key) => ({
        url: `/api/v1/user/settings/${key}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_result, _error, key) => [{ type: 'UserSettings' as const, id: key }],
    }),
  })
});

export const { 
  useResetUserPasswordMutation,
  useSetSettingsMutation,
  useGetUsersQuery, 
  useGetUserRolesQuery,
  useGetAllRolesQuery,
  useCreateUserMutation,
  useSetUserRoleMutation,
  useDeleteUserMutation,

  useGetUserSettingQuery,
  useCreateUserSettingMutation,
  useUpdateUserSettingMutation,
  useDeleteUserSettingMutation,
} = settingApi;